import type { CSSProperties, JSX, KeyboardEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { FAB_DRAG_THRESHOLD, fabPosFromDrop, loadFabPos, saveFabPos, type FabPos } from '../lib/help-fab';
import { plural } from '../lib/hub-home';
import { sentDueText } from '../lib/support-format';
import { FeedbackForm, type FormKind, type SentInfo } from './feedback/FeedbackForm';
import { IikoMineList, MineDetail, MineList } from './feedback/FeedbackMine';
import {
  IconArrowLeft,
  IconBookOpen,
  IconBug,
  IconCircleHelp,
  IconClipboardList,
  IconExternalLink,
  IconLightbulb,
  IconMaximize,
  IconMinimize,
  IconPlus,
  IconX,
} from './feedback/feedback-icons';
import { captureScreen } from './feedback/feedback-capture';
import { HUB_FEEDBACK_EVENT, feedbackViewOf, type FeedbackView } from './feedback/feedback-open';
import { Pill } from './feedback/feedback-ui';
import { useFeedbackGate } from './feedback/useFeedbackGate';
import './feedback/feedback.css';

/**
 * Плавающий «+» в правом нижнем углу HUB — общий с CRM и SMS-центром (владелец
 * 19.10.2026: «один общий плюсик, устроен как в CRM»; образец —
 * frontend/src/components/feedback/FeedbackWidget.tsx). Оранжевая кнопка 52 px,
 * «+» поворачивается в «×»; клик раскрывает стопку белых плашек, форма открывается
 * шторкой 400 px (можно развернуть на весь экран, на телефоне — всегда на весь).
 *
 * Плашки: «Вопрос в УК» (если CRM его не выключила: `questionEnabled`) · «Сообщить об
 * ошибке» · «Предложить идею» · «Мои обращения» · «Задать вопрос по базе знаний»
 * (только HUB, → /knowledge/ask). Два вида зрителя (WikiMeDto.type):
 *   crm_user      — ручки CRM `/api/feedback*` (тот же origin через nginx, cookie fib.sid):
 *                   «Кому уйдёт вопрос» с выбором отдела, любое число файлов, лента и ответ
 *                   команде. Партнёру «Мои обращения» открывают CRM → кабинет партнёра.
 *   iiko_employee — без аккаунта CRM: прежний путь `hubApi.sendFeedback` (/api/wiki/hub/feedback),
 *                   свои «Мои обращения» (статус и ответ УК); «Вопрос в УК» — это «Обращение в УК».
 *
 * Кнопку можно перетащить мышью или пальцем к левому или правому краю — место
 * (край + отступ снизу) запоминается в браузере (lib/help-fab.ts); двойной клик
 * возвращает её в угол. На телефоне стоит над нижней панелью вкладок. Положение
 * и подъём над липкими панелями страниц задаёт CSS `.fk-hub-fab-dock` (styles/global.css),
 * стопка плашек и шторка висят над кнопкой (components/feedback/feedback.css).
 * Открыть извне: `openHubFeedback('bug' | …)` из components/feedback/feedback-open.
 */

type View = FeedbackView | 'detail' | 'sent';

const CRM_REQUESTS_URL = 'https://crm.brasol.ru/partner/dashboard?tab=requests';

const DEFAULT_TEXTS = {
  bugIntro: 'Что-то не работает или показывает неверно',
  ideaIntro: 'Идея для базы знаний, курсов или HUB',
  slaText: 'Ответим в течение рабочего дня',
};

const TITLES: Record<Exclude<View, 'menu'>, string> = {
  question: 'Вопрос в управляющую компанию',
  bug: 'Сообщить об ошибке',
  idea: 'Предложить идею',
  mine: 'Мои обращения',
  detail: 'Обращение',
  sent: 'Спасибо!',
};

/**
 * «Пол» кнопки в пикселях — ровно то, от чего CSS отсчитывает отступ
 * (--fk-fab-floor в global.css): нижняя панель телефона или «домашняя
 * полоска» iPhone/iPad, плюс подъём над липкой панелью страницы (тест,
 * «Сохранить»). Меряем невидимым замером внутри кнопки, а не считаем сами:
 * иначе отпущенная кнопка подпрыгивает вверх на величину безопасной зоны
 * или подъёма. getComputedStyle, а не getBoundingClientRect — на нажатую
 * кнопку действует scale, он исказил бы замер.
 */
function floorHeight(probe: HTMLElement | null): number {
  if (!probe) return 0;
  return parseFloat(getComputedStyle(probe).height) || 0;
}

/** Сколько сверху занимает липкий верх — шапка и плашки (+ небольшой зазор). */
function headerReserve(): number {
  const el = document.querySelector('.fk-hub-top');
  return (el ? el.getBoundingClientRect().height : 59) + 8;
}

const NARROW_QUERY = '(max-width: 767px)';

function Plate({ icon, tone, onClick, badge, children }: { icon: ReactNode; tone: string; onClick: () => void; badge?: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <button type="button" role="menuitem" className="fk-hub-fb-plate" onClick={onClick}>
      <span className="fk-hub-fb-plate-ic" style={{ color: tone }}>
        {icon}
      </span>
      <span className="fk-hub-fb-plate-label">{children}</span>
      {badge}
    </button>
  );
}

function IconBtn({ title, onClick, children }: { title: string; onClick: () => void; children: ReactNode }): JSX.Element {
  return (
    <button type="button" className="fk-hub-fb-iconbtn" title={title} aria-label={title} onClick={onClick}>
      {children}
    </button>
  );
}

export function HelpFab(): JSX.Element | null {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me, isIiko, partner, info, feedbackOn, questionOn } = useFeedbackGate();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('menu');
  const [expanded, setExpanded] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [sent, setSent] = useState<SentInfo | null>(null);
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches);
  const [pos, setPos] = useState<FabPos | null>(() => loadFabPos());
  // Идёт снимок экрана: кнопка и окно формы спрятаны (CSS [data-capturing]), иначе они попали бы в кадр.
  const [capturing, setCapturing] = useState(false);
  // Перетаскивание: точка нажатия; moved — чтобы отличить клик от переноса.
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const [dragAt, setDragAt] = useState<{ x: number; y: number; size: number } | null>(null);
  const suppressClick = useRef(false);
  const floorProbe = useRef<HTMLSpanElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Меню, открытое с клавиатуры, сразу получает фокус (стрелки, Enter); мышью — фокус остаётся на кнопке.
  const keyboardOpen = useRef(false);

  const texts = info?.texts ?? DEFAULT_TEXTS;
  const counts = info?.counts;
  const badge = feedbackOn && counts ? counts.verify + counts.unread : 0;

  const side = pos?.side ?? 'right';
  const full = expanded || narrow;

  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const on = (): void => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  // Снимок экрана для формы «Сообщить об ошибке»: прячем «+» и окно (состояние формы остаётся),
  // даём браузеру убрать их с экрана, снимаем, возвращаем. Прячем и на время вопроса браузера «какую вкладку показать».
  const onCapture = useCallback(async (): Promise<string | null> => {
    setCapturing(true);
    try {
      await new Promise((r) => setTimeout(r, 150));
      return await captureScreen();
    } finally {
      setCapturing(false);
    }
  }, []);

  const close = useCallback((): void => {
    setOpen(false);
    setExpanded(false);
    setView('menu');
  }, []);

  // Что можно открыть сейчас; обработчик события читает свежие значения через ref.
  const gate = useRef({ feedbackOn, questionOn, partner });
  gate.current = { feedbackOn, questionOn, partner };

  // «Мои обращения»: партнёру — в кабинет партнёра CRM (новая вкладка), остальным — список здесь.
  const openMine = useCallback((): void => {
    if (gate.current.partner) {
      close();
      window.open(CRM_REQUESTS_URL, '_blank', 'noopener');
    } else {
      setView('mine');
      setOpen(true);
    }
  }, [close]);

  // Открытие извне (openHubFeedback): «Создать ▾» в шапке.
  useEffect(() => {
    const h = (e: Event): void => {
      const g = gate.current;
      let v = feedbackViewOf((e as CustomEvent).detail);
      if (!g.feedbackOn) v = 'menu';
      else if (v === 'question' && !g.questionOn) v = 'menu';
      setExpanded(false);
      if (v === 'mine') {
        openMine();
        return;
      }
      setView(v);
      setOpen(true);
    };
    window.addEventListener(HUB_FEEDBACK_EVENT, h);
    return () => window.removeEventListener(HUB_FEEDBACK_EVENT, h);
  }, [openMine]);

  // Esc — свернуть с полного экрана, потом закрыть.
  useEffect(() => {
    if (!open) return;
    const h = (e: globalThis.KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (expanded) setExpanded(false);
      else {
        close();
        btnRef.current?.focus();
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, expanded, close]);

  // Стопка плашек закрывается кликом мимо (форму — нет: в ней может быть набранный текст).
  useEffect(() => {
    if (!open || view !== 'menu') return;
    const h = (e: PointerEvent): void => {
      if (e.target instanceof Node && !dockRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', h);
    return () => document.removeEventListener('pointerdown', h);
  }, [open, view]);

  // Меню, открытое с клавиатуры, — фокус на первую плашку.
  useEffect(() => {
    if (open && view === 'menu' && keyboardOpen.current) {
      keyboardOpen.current = false;
      menuRef.current?.querySelector<HTMLElement>('button')?.focus();
    }
  }, [open, view]);

  if (!me) return null;

  const go = (v: FeedbackView): void => {
    setView(v);
    setOpen(true);
  };

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('button') ?? []);
    if (!items.length) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
    items[next].focus();
  };

  const subtitle = (v: Exclude<View, 'menu'>): string => {
    switch (v) {
      case 'bug':
        return texts.bugIntro;
      case 'idea':
        return texts.ideaIntro;
      case 'question':
        return isIiko ? 'Напишите своими словами — обращение попадёт в управляющую компанию' : 'Напишите своими словами — CRM сама поймёт, какой отдел ответит';
      case 'mine':
        return isIiko ? 'Статусы и ответы УК — здесь' : 'Статусы и ответы команды — здесь и в колокольчике CRM';
      default:
        return '';
    }
  };

  // Пока тащим — кнопка под пальцем; иначе место задаёт CSS (край + отступ).
  const dockStyle: CSSProperties = dragAt
    ? { left: dragAt.x - dragAt.size / 2, top: dragAt.y - dragAt.size / 2, right: 'auto', bottom: 'auto' }
    : pos
      ? ({ '--fk-fab-offset': `${pos.offset}px` } as CSSProperties)
      : {};

  const mineBadge: ReactNode =
    counts && counts.verify ? (
      <Pill tone="var(--fk-warning-text)">{counts.verify} на проверке</Pill>
    ) : counts && counts.unread ? (
      <Pill tone="var(--fk-orange-hover)">
        {counts.unread} {plural(counts.unread, ['ответ', 'ответа', 'ответов'])}
      </Pill>
    ) : counts && counts.open ? (
      <span className="fk-hub-fb-muted">{counts.open}</span>
    ) : null;

  const panelView = view === 'menu' ? null : view;

  return (
    <div
      ref={dockRef}
      className="fk-hub-fab-dock"
      data-side={side}
      data-dragging={dragAt ? 'true' : 'false'}
      data-open={open ? 'true' : 'false'}
      data-capturing={capturing ? 'true' : 'false'}
      style={dockStyle}
    >
      {open && view === 'menu' ? (
        <div ref={menuRef} className="fk-hub-fb-menu" role="menu" aria-label="Обращения" onKeyDown={onMenuKeyDown}>
          {questionOn ? (
            <Plate icon={<IconCircleHelp size={18} />} tone="var(--fk-info)" onClick={() => go('question')}>
              Вопрос в УК
            </Plate>
          ) : null}
          {feedbackOn ? (
            <>
              <Plate icon={<IconBug size={18} />} tone="var(--fk-error)" onClick={() => go('bug')}>
                Сообщить об ошибке
              </Plate>
              <Plate icon={<IconLightbulb size={18} />} tone="var(--fk-success-text)" onClick={() => go('idea')}>
                Предложить идею
              </Plate>
              <Plate
                icon={<IconClipboardList size={18} />}
                tone="var(--fk-indigo)"
                onClick={openMine}
                badge={
                  <>
                    {mineBadge}
                    {partner ? <IconExternalLink size={13} /> : null}
                  </>
                }
              >
                Мои обращения
              </Plate>
            </>
          ) : null}
          <Plate
            icon={<IconBookOpen size={18} />}
            tone="var(--fk-chart-5)"
            onClick={() => {
              setOpen(false);
              navigate('/knowledge/ask');
            }}
          >
            Задать вопрос по базе знаний
          </Plate>
        </div>
      ) : null}

      {open && panelView ? (
        <>
          {full ? <div className="fk-hub-fb-backdrop" onClick={() => setExpanded(false)} /> : null}
          <div className="fk-hub-fb-panel" role="dialog" aria-label={TITLES[panelView]} data-full={full ? 'true' : 'false'} data-narrow={narrow ? 'true' : 'false'}>
            <div className="fk-hub-fb-head">
              {panelView === 'detail' || panelView === 'bug' || panelView === 'idea' || panelView === 'question' ? (
                <IconBtn title="Назад" onClick={() => setView(panelView === 'detail' ? 'mine' : 'menu')}>
                  <IconArrowLeft size={14} />
                </IconBtn>
              ) : null}
              <div className="fk-hub-fb-head-text">
                <div className="fk-hub-fb-title">{TITLES[panelView]}</div>
                {subtitle(panelView) ? <div className="fk-hub-fb-subtitle">{subtitle(panelView)}</div> : null}
              </div>
              {!narrow ? (
                <IconBtn title={expanded ? 'Свернуть' : 'Развернуть на весь экран'} onClick={() => setExpanded(!expanded)}>
                  {expanded ? <IconMinimize size={14} /> : <IconMaximize size={14} />}
                </IconBtn>
              ) : null}
              <IconBtn title="Закрыть" onClick={close}>
                <IconX size={14} />
              </IconBtn>
            </div>
            <div className="fk-hub-fb-body">
              {panelView === 'bug' || panelView === 'idea' || panelView === 'question' ? (
                <FeedbackForm
                  key={panelView}
                  kind={panelView as FormKind}
                  mode={isIiko ? 'iiko' : 'crm'}
                  roleName={me.roleName ?? null}
                  draftOwner={me.userId ?? me.email ?? null}
                  full={full}
                  sla={texts.slaText}
                  onCapture={onCapture}
                  onSent={(r) => {
                    setSent(r);
                    setView('sent');
                    void qc.invalidateQueries({ queryKey: ['wiki', 'feedback', 'widget'] });
                  }}
                />
              ) : null}
              {panelView === 'mine' ? (
                isIiko ? (
                  <IikoMineList onNew={(k) => setView(k)} />
                ) : (
                  <MineList
                    onOpen={(id) => {
                      setDetailId(id);
                      setView('detail');
                    }}
                    onNew={(k) => setView(k)}
                  />
                )
              ) : null}
              {panelView === 'detail' && detailId ? (
                <MineDetail id={detailId} full={full} onChanged={() => void qc.invalidateQueries({ queryKey: ['wiki', 'feedback', 'widget'] })} />
              ) : null}
              {panelView === 'sent' && sent ? (
                <div className="fk-hub-fb-sent">
                  {sent.question ? (
                    <>
                      <div className="fk-hub-fb-sent-main">
                        Вопрос <b className="fk-hub-fb-mono">{sent.code}</b> отправлен в отдел «{sent.question.deptLabel}».{' '}
                        {sent.question.assigneeName ? `Ответит ${sent.question.assigneeName}.` : 'Руководитель отдела назначит ответственного.'}
                      </div>
                      <div className="fk-hub-fb-sub">{sentDueText(sent.question)} Ответ придёт в колокольчик CRM; статус — в «Мои обращения».</div>
                    </>
                  ) : isIiko ? (
                    <>
                      <div className="fk-hub-fb-sent-main">
                        Обращение <b className="fk-hub-fb-mono">{sent.code}</b> отправлено в управляющую компанию.
                      </div>
                      <div className="fk-hub-fb-sub">{texts.slaText}. Ответ появится здесь, в «Мои обращения».</div>
                    </>
                  ) : (
                    <>
                      <div className="fk-hub-fb-sent-main">
                        Обращение <b className="fk-hub-fb-mono">{sent.code}</b> отправлено — оно уже на доске команды как «Новое».
                      </div>
                      <div className="fk-hub-fb-sub">{texts.slaText}. Ответ придёт в колокольчик CRM; статус — в «Мои обращения».</div>
                    </>
                  )}
                  <div className="fk-hub-fb-sent-actions">
                    {isIiko ? (
                      <button type="button" className="fk-hub-fb-tool fk-hub-fb-tool--primary" onClick={() => setView('mine')}>
                        Мои обращения
                      </button>
                    ) : partner ? (
                      <button type="button" className="fk-hub-fb-tool fk-hub-fb-tool--primary" onClick={openMine}>
                        Мои обращения
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="fk-hub-fb-tool fk-hub-fb-tool--primary"
                        onClick={() => {
                          setDetailId(sent.id);
                          setView('detail');
                        }}
                      >
                        Открыть обращение
                      </button>
                    )}
                    <button type="button" className="fk-hub-fb-tool" onClick={close}>
                      Готово
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      <button
        ref={btnRef}
        type="button"
        className="fk-hub-fab"
        aria-label={open ? 'Закрыть обращения' : 'Обращения: вопрос в УК, ошибка, идея, мои обращения'}
        aria-haspopup="menu"
        aria-expanded={open}
        title={open ? 'Закрыть' : 'Вопрос в УК, ошибка, идея · кнопку можно перетащить, двойной клик — на место'}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            // С клавиатуры перетаскивания не бывает — клик не глушим.
            suppressClick.current = false;
            keyboardOpen.current = true;
          }
        }}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          // Новый жест — прошлый «клик после переноса» больше не глушим.
          suppressClick.current = false;
          keyboardOpen.current = false;
          drag.current = { x: e.clientX, y: e.clientY, moved: false };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < FAB_DRAG_THRESHOLD) return;
          if (!d.moved) close();
          d.moved = true;
          setDragAt({ x: e.clientX, y: e.clientY, size: e.currentTarget.offsetWidth });
        }}
        onPointerUp={(e) => {
          const d = drag.current;
          drag.current = null;
          setDragAt(null);
          if (!d?.moved) return;
          suppressClick.current = true;
          const next = fabPosFromDrop({
            x: e.clientX,
            y: e.clientY,
            viewportW: window.innerWidth,
            viewportH: window.innerHeight,
            size: e.currentTarget.offsetWidth,
            baseBottom: floorHeight(floorProbe.current),
            topReserve: headerReserve(),
          });
          setPos(next);
          saveFabPos(next);
        }}
        onPointerCancel={() => {
          drag.current = null;
          setDragAt(null);
        }}
        onDoubleClick={() => {
          close();
          setPos(null);
          saveFabPos(null);
        }}
        onClick={() => {
          // Клик, которым закончилось перетаскивание, меню не открывает.
          if (suppressClick.current) {
            suppressClick.current = false;
            return;
          }
          if (open) close();
          else go('menu');
        }}
      >
        <span className="fk-hub-fab-ic">
          <IconPlus size={26} />
        </span>
        {!open && badge > 0 ? <span className="fk-hub-fab-badge">{badge}</span> : null}
        <span ref={floorProbe} className="fk-hub-fab-floor" aria-hidden="true" />
      </button>
    </div>
  );
}
